import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, Typography } from 'antd';
import { SearchOutlined, DownOutlined } from '@ant-design/icons';
import './EducationDropdown.css';

const { Title } = Typography;

interface DropdownOption {
  key: string;
  label: string;
}

interface EducationDropdownProps {
  options: DropdownOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  title?: string;
  className?: string;
  disabled?: boolean;
}

const EducationDropdown: React.FC<EducationDropdownProps> = ({
  title = "Select Option",
  options,
  value,
  onChange,
  placeholder = "Select an option",
  className = '',
  disabled = false
}) => {
  const [visible, setVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const buttonRef = useRef<HTMLDivElement>(null);

  // Reset search term when dropdown closes
  useEffect(() => {
    if (!visible) {
      setSearchTerm('');
    }
  }, [visible]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current && !buttonRef.current.contains(target)) {
        // Check if click is inside the dropdown overlay
        const overlay = document.querySelector('.education-dropdown-overlay');
        if (overlay && !overlay.contains(target)) {
          setVisible(false);
        }
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible]);

  const handleOpen = () => {
    if (!disabled) {
      setVisible(true);
    }
  };

  const handleClose = () => {
    setVisible(false);
  };

  const handleSelect = (option: DropdownOption) => {
    console.log('Selecting option:', option);
    onChange(option.key);
    handleClose();
  };

  const handleClear = () => {
    onChange('');
  };

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(option => option.key === value);

  return (
    <div className="education-dropdown-container">
      <div 
        className={`education-dropdown-button ${disabled ? 'disabled' : ''} ${className}`}
        onClick={handleOpen}
        ref={buttonRef}
      >
        <span className="dropdown-text">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <DownOutlined className="dropdown-icon" />
      </div>
      
      {visible && (
        <div className="education-dropdown-overlay">
          <div className="education-modal-content">
            <div className="education-modal-header">
              <Title level={4} className="education-modal-title">{title}</Title>
              <Button 
                type="text" 
                className="clear-btn"
                onClick={handleClear}
              >
                Clear
              </Button>
            </div>
            
            <div className="education-search-section">
              <Input
                placeholder="Search"
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="education-search-input"
              />
            </div>
            
            <div className="education-options-section">
              {filteredOptions.length > 0 ? (
                filteredOptions.map(option => (
                  <div 
                    key={option.key} 
                    className={`education-option-item ${value === option.key ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Option clicked:', option);
                      handleSelect(option);
                    }}
                  >
                    {option.label}
                  </div>
                ))
              ) : (
                <div className="education-no-options">
                  No options found
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EducationDropdown; 